import React, { useState } from 'react';
import { Star, ThumbsUp, ThumbsDown, MessageCircle, CheckCircle, X, ChevronRight, Check } from 'lucide-react';
import { feedbackService } from '../../services/feedbackService';
import type { CreateFeedbackRequest, SectionFeedback } from '../../services/feedbackService';
import { useAuthStore } from '../../store/authStore';

interface FeedbackFormProps {
  assessmentId: string;
  assessmentType?: string;
  onClose: () => void;
  onSubmitted?: () => void;
}



const IMPROVEMENT_AREAS = [
  'Mais exemplos práticos',
  'Conteúdo mais personalizado',
  'Estratégias mais específicas',
  'Recursos adicionais',
  'Interface mais intuitiva',
  'Explicações mais claras',
  'Plano de ação mais detalhado',
  'Feedback mais visual',
];

type TabType = 'general' | 'improvements';

interface ImprovementAreaSelectorProps {
  selectedAreas: string[];
  customSuggestion: string;
  onAreaToggle: (area: string) => void;
  onCustomSuggestionChange: (value: string) => void;
}

const ImprovementAreaSelector: React.FC<ImprovementAreaSelectorProps> = ({
  selectedAreas,
  customSuggestion,
  onAreaToggle,
  onCustomSuggestionChange
}) => (
  <div className="space-y-3">
    <div>
      <h4 className="font-medium text-neutral-900 mb-2">
        Que áreas gostaríamos de melhorar? (Selecione todas que se aplicam)
      </h4>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
        {IMPROVEMENT_AREAS.map((area) => {
          const isSelected = selectedAreas.includes(area);
          return (
            <button
              key={area}
              type="button"
              onClick={() => onAreaToggle(area)}
              className={`flex items-center gap-2 p-2 text-left rounded-md border transition-colors text-sm ${
                isSelected
                  ? 'border-primary-500 bg-primary-50 text-primary-700'
                  : 'border-neutral-300 hover:border-primary-300 hover:bg-neutral-50'
              }`}
            >
              <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                isSelected 
                  ? 'border-primary-500 bg-primary-500' 
                  : 'border-neutral-300'
              }`}>
                {isSelected && <Check size={10} className="text-white" />}
              </div>
              <span className="text-xs leading-tight">{area}</span>
            </button>
          );
        })}
      </div>
    </div>

    <div className="space-y-1">
      <label className="block text-sm font-medium text-neutral-900">
        Outras sugestões de melhoria (opcional)
      </label>
      <textarea
        value={customSuggestion}
        onChange={(e) => onCustomSuggestionChange(e.target.value)}
        rows={2}
        className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
        placeholder="Descreva outras melhorias que gostaria de ver..."
      />
    </div>
  </div>
);

export const FeedbackForm: React.FC<FeedbackFormProps> = ({ 
  assessmentId,
  assessmentType = 'communication-profile',
  onClose, 
  onSubmitted 
}) => {
  const { user } = useAuthStore();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [currentTab, setCurrentTab] = useState<TabType>('general');
  
  // Estado do feedback geral
  const [overallRating, setOverallRating] = useState(0);
  const [accuracyRating, setAccuracyRating] = useState(0);
  const [satisfactionRating, setSatisfactionRating] = useState(0);
  const [generalComment, setGeneralComment] = useState('');
  const [wouldRecommend, setWouldRecommend] = useState<boolean | null>(null);

  // Estado do feedback de melhorias
  const [selectedImprovementAreas, setSelectedImprovementAreas] = useState<string[]>([]);
  const [customImprovementSuggestion, setCustomImprovementSuggestion] = useState('');

  const handleImprovementAreaToggle = (area: string) => {
    setSelectedImprovementAreas(prev => 
      prev.includes(area) 
        ? prev.filter(a => a !== area)
        : [...prev, area]
    );
  };

  // Validação para submissão
  const isGeneralTabValid = overallRating > 0 && accuracyRating > 0 && satisfactionRating > 0 && wouldRecommend !== null;
  
  const getTabCompletionStatus = (tab: TabType): 'completed' | 'partial' | 'empty' => {
    switch (tab) {
      case 'general':
        return isGeneralTabValid ? 'completed' : (overallRating > 0 ? 'partial' : 'empty');
      case 'improvements':
        return selectedImprovementAreas.length > 0 || customImprovementSuggestion.trim() ? 'completed' : 'empty';
    }
  };

  const handleSubmit = async () => {
    if (!isGeneralTabValid) {
      alert('Por favor, preencha todos os campos obrigatórios na aba Avaliação Geral.');
      setCurrentTab('general');
      return;
    }

    setIsSubmitting(true);
    try {
      // Consolidar áreas de melhoria
      const allImprovementAreas = [...selectedImprovementAreas];
      if (customImprovementSuggestion.trim()) {
        allImprovementAreas.push(customImprovementSuggestion.trim());
      }

      const feedbackData: CreateFeedbackRequest = {
        assessmentId,
        assessmentType,
        userContext: user ? {
          id: user.id || '',
          name: user.name,
          email: user.email,
          profileImage: user.profile_image,
          subscriptionTier: user.subscription_tier,
        } : undefined,
        overallRating,
        accuracyRating,
        satisfactionRating,
        generalComment,
        sectionFeedbacks: {}, // Removido - seções não são mais avaliadas
        usefulnessFlags: {}, // Para uso futuro
        improvementAreas: allImprovementAreas,
        wouldRecommend,
        submittedAt: new Date().toISOString(),
        userAgent: navigator.userAgent,
      };

      await feedbackService.createFeedback(feedbackData);
      setIsSubmitted(true);
      onSubmitted?.();
    } catch (error) {
      console.error('Erro ao enviar feedback:', error);
      alert('Erro ao enviar feedback. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const StarRating: React.FC<{ 
    rating: number; 
    onRatingChange: (rating: number) => void; 
    label: string;
    required?: boolean;
  }> = ({ rating, onRatingChange, label, required = false }) => (
    <div className="space-y-1">
      <label className="block text-sm font-medium text-neutral-900">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onRatingChange(star)}
            className="p-0.5 transition-colors"
          >
            <Star
              size={20}
              className={
                star <= rating
                  ? 'fill-yellow-400 text-yellow-400'
                  : 'text-neutral-300 hover:text-yellow-300'
              }
            />
          </button>
        ))}
      </div>
    </div>
  );





  if (isSubmitted) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl p-8 max-w-md w-full text-center">
          <div className="flex justify-center mb-4">
            <CheckCircle size={64} className="text-green-500" />
          </div>
          <h2 className="text-2xl font-bold text-neutral-900 mb-2">
            Obrigado pelo seu feedback!
          </h2>
          <p className="text-neutral-600 mb-6">
            Suas opiniões são valiosas e nos ajudam a melhorar continuamente nossos serviços.
          </p>
          <button
            onClick={onClose}
            className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition-colors"
          >
            Fechar
          </button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'general' as TabType, label: 'Avaliação Geral', required: true },
    { id: 'improvements' as TabType, label: 'Sugestões de Melhoria', required: false },
  ];

  const renderTabContent = () => {
    switch (currentTab) {
      case 'general':
        return (
          <div className="space-y-3">
            <StarRating
              rating={overallRating}
              onRatingChange={setOverallRating}
              label="Como você avalia sua experiência geral?"
              required
            />
            
            <StarRating
              rating={accuracyRating}
              onRatingChange={setAccuracyRating}
              label="Quão precisos foram os resultados em relação à sua realidade?"
              required
            />
            
            <StarRating
              rating={satisfactionRating}
              onRatingChange={setSatisfactionRating}
              label="Qual seu nível de satisfação com o relatório?"
              required
            />

            <div className="space-y-2">
              <label className="block text-sm font-medium text-neutral-900">
                Você recomendaria a um amigo ou colega? <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setWouldRecommend(true)}
                  className={`flex items-center gap-2 p-2 rounded-lg border-2 transition-colors ${
                    wouldRecommend === true
                      ? 'border-green-500 bg-green-50'
                      : 'border-neutral-300 hover:border-green-300'
                  }`}
                >
                  <ThumbsUp size={18} className={wouldRecommend === true ? 'text-green-600' : ''} />
                  <span className="text-sm">Sim</span>
                </button>
                <button
                  type="button"
                  onClick={() => setWouldRecommend(false)}
                  className={`flex items-center gap-2 p-2 rounded-lg border-2 transition-colors ${
                    wouldRecommend === false
                      ? 'border-red-500 bg-red-50'
                      : 'border-neutral-300 hover:border-red-300'
                  }`}
                >
                  <ThumbsDown size={18} className={wouldRecommend === false ? 'text-red-600' : ''} />
                  <span className="text-sm">Não</span>
                </button>
              </div>
            </div>

            <div className="space-y-1">
              <label className="block text-sm font-medium text-neutral-900">
                Comentários gerais (opcional)
              </label>
              <textarea
                value={generalComment}
                onChange={(e) => setGeneralComment(e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                placeholder="Compartilhe sua experiência, sugestões ou comentários..."
              />
            </div>
          </div>
        );

      case 'improvements':
        return (
          <div>
            <div className="mb-3">
              <h3 className="text-lg font-medium text-neutral-900 mb-1">
                Como Podemos Melhorar?
              </h3>
              <p className="text-xs text-neutral-600">
                Suas sugestões são fundamentais para evoluirmos nosso produto
              </p>
            </div>
            <ImprovementAreaSelector
              selectedAreas={selectedImprovementAreas}
              customSuggestion={customImprovementSuggestion}
              onAreaToggle={handleImprovementAreaToggle}
              onCustomSuggestionChange={setCustomImprovementSuggestion}
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 md:p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[98vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-3 border-b border-neutral-200">
          <div>
            <h2 className="text-lg font-bold text-neutral-900">
              Como foi sua experiência?
            </h2>
            <p className="text-xs text-neutral-600">
              Seu feedback nos ajuda a melhorar nossos serviços
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-neutral-100 rounded-lg transition-colors flex-shrink-0"
          >
            <X size={18} className="text-neutral-500" />
          </button>
        </div>

        {/* Tabs Navigation */}
        <div className="border-b border-neutral-200">
          <div className="flex">
            {tabs.map((tab) => {
              const completionStatus = getTabCompletionStatus(tab.id);
              const isActive = currentTab === tab.id;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => setCurrentTab(tab.id)}
                  className={`flex-1 px-3 py-2 text-sm font-medium border-b-2 transition-colors relative ${
                    isActive
                      ? 'border-primary-600 text-primary-600'
                      : 'border-transparent text-neutral-600 hover:text-neutral-900 hover:border-neutral-300'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    {tab.label}
                    {tab.required && <span className="text-red-500">*</span>}
                    {completionStatus === 'completed' && (
                      <Check size={16} className="text-green-500" />
                    )}
                  </div>
                  {completionStatus === 'partial' && (
                    <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-yellow-500 rounded-full -mb-1" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="p-3 flex-1 min-h-0 overflow-y-auto">
          {renderTabContent()}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center p-3 border-t border-neutral-200 bg-neutral-50">
          <div className="flex gap-2">
            {currentTab !== 'general' && (
              <button
                onClick={() => {
                  const currentIndex = tabs.findIndex(t => t.id === currentTab);
                  if (currentIndex > 0) {
                    setCurrentTab(tabs[currentIndex - 1].id);
                  }
                }}
                className="px-4 py-2 text-neutral-600 hover:text-neutral-900 transition-colors"
              >
                Anterior
              </button>
            )}
            {currentTab !== 'improvements' && (
              <button
                onClick={() => {
                  const currentIndex = tabs.findIndex(t => t.id === currentTab);
                  if (currentIndex < tabs.length - 1) {
                    setCurrentTab(tabs[currentIndex + 1].id);
                  }
                }}
                className="px-4 py-2 bg-neutral-200 text-neutral-700 rounded-lg hover:bg-neutral-300 transition-colors flex items-center gap-1"
              >
                Próximo
                <ChevronRight size={16} />
              </button>
            )}
          </div>
          
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !isGeneralTabValid}
            className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:bg-neutral-400 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isSubmitting ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                <MessageCircle size={16} />
                Enviar Feedback
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};